import React, { useMemo, useState } from 'react';
import { useStore } from 'zustand';
import { useTheme, alpha } from '@mui/material';
import { useUnityBuildStore } from '../../stores/store';
import { useAssemblyStore } from '../../stores/AssemblyStore';
import { calculateRealWorldDistance } from './takeoffHelper';

interface AreaDrawingEnhancementsProps {
  cursorPositionRef: React.MutableRefObject<{ x: number; y: number }>;
}

/**
 * Enhanced Area Drawing Features for Professional Construction Takeoffs
 *
 * Additional features:
 * - Segment length labels
 * - Corner angle indicators
 * - Grid snap visualization
 * - Area breakdown by segments
 * - Professional construction drawing annotations
 */
export const AreaDrawingEnhancements: React.FC<AreaDrawingEnhancementsProps> = ({
  cursorPositionRef,
}) => {
  const theme = useTheme();
  const { currentTakeoffAssembly } = useStore(useAssemblyStore);
  const { userScale, temporaryPoints, isAnnotatingTakeoff } = useStore(useUnityBuildStore);

  const [showSegmentLabels] = useState(true);
  const [showAngles] = useState(false);

  // Only render for area type assemblies
  const shouldRender = useMemo(() => {
    return currentTakeoffAssembly?.assemblymeasurementtype === 'area' && isAnnotatingTakeoff;
  }, [currentTakeoffAssembly, isAnnotatingTakeoff]);

  // Get working points
  const workingPoints = useMemo(() => {
    if (currentTakeoffAssembly?.pointsList?.length) {
      return currentTakeoffAssembly.pointsList;
    }
    return temporaryPoints || [];
  }, [currentTakeoffAssembly, temporaryPoints]);

  // Calculate segment lengths and angles
  const segmentData = useMemo(() => {
    if (workingPoints.length < 2) return [];

    const segments = [];
    for (let i = 0; i < workingPoints.length; i++) {
      const current = workingPoints[i];
      const next = workingPoints[(i + 1) % workingPoints.length];

      // Skip the closing segment if we don't have a complete polygon
      if (i === workingPoints.length - 1 && workingPoints.length < 3) break;

      const dx = next.x - current.x;
      const dy = next.y - current.y;
      const pixelLength = Math.sqrt(dx * dx + dy * dy);
      const realLength = calculateRealWorldDistance(pixelLength, userScale);

      // Calculate angle
      const angle = Math.atan2(dy, dx) * (180 / Math.PI);

      // Calculate midpoint for label placement
      const midX = (current.x + next.x) / 2;
      const midY = (current.y + next.y) / 2;

      // Calculate perpendicular offset for label positioning
      const perpAngle = angle + 90;
      const offsetDistance = 20;
      const labelX = midX + Math.cos((perpAngle * Math.PI) / 180) * offsetDistance;
      const labelY = midY + Math.sin((perpAngle * Math.PI) / 180) * offsetDistance;

      segments.push({
        index: i,
        start: current,
        end: next,
        length: realLength,
        pixelLength,
        angle,
        midX,
        midY,
        labelX,
        labelY,
      });
    }

    return segments;
  }, [workingPoints, userScale]);

  // Render segment length labels
  const renderSegmentLabels = useMemo(() => {
    if (!showSegmentLabels || segmentData.length === 0) return null;

    return (
      <g key="segment-labels">
        {segmentData.map(segment => {
          const lengthText = `${segment.length.toFixed(2)}'`;

          return (
            <g key={`segment-label-${segment.index}`}>
              {/* Label background */}
              <rect
                x={segment.labelX - 18}
                y={segment.labelY - 8}
                width={36}
                height={16}
                fill={alpha(theme.palette.background.paper, 0.9)}
                stroke={theme.palette.divider}
                strokeWidth={1}
                rx={3}
                ry={3}
              />

              {/* Length text */}
              <text
                x={segment.labelX}
                y={segment.labelY + 2}
                textAnchor="middle"
                fill={theme.palette.text.primary}
                fontSize="10"
                fontWeight="500"
                fontFamily="monospace"
              >
                {lengthText}
              </text>

              {/* Leader line to segment */}
              <line
                x1={segment.midX}
                y1={segment.midY}
                x2={segment.labelX}
                y2={segment.labelY}
                stroke={theme.palette.divider}
                strokeWidth={1}
                strokeDasharray="2,2"
                opacity={0.6}
              />
            </g>
          );
        })}
      </g>
    );
  }, [showSegmentLabels, segmentData, theme]);

  // Render corner angle indicators
  const renderAngleIndicators = useMemo(() => {
    if (!showAngles || workingPoints.length < 3) return null;

    const angles = [];
    for (let i = 0; i < workingPoints.length; i++) {
      const prev = workingPoints[(i - 1 + workingPoints.length) % workingPoints.length];
      const current = workingPoints[i];
      const next = workingPoints[(i + 1) % workingPoints.length];

      // Calculate vectors
      const v1 = { x: prev.x - current.x, y: prev.y - current.y };
      const v2 = { x: next.x - current.x, y: next.y - current.y };

      // Calculate angle between vectors
      const dot = v1.x * v2.x + v1.y * v2.y;
      const mag1 = Math.sqrt(v1.x * v1.x + v1.y * v1.y);
      const mag2 = Math.sqrt(v2.x * v2.x + v2.y * v2.y);
      const cosAngle = dot / (mag1 * mag2);
      const angle = Math.acos(Math.max(-1, Math.min(1, cosAngle))) * (180 / Math.PI);

      angles.push({
        point: current,
        angle: angle.toFixed(1),
        index: i,
      });
    }

    return (
      <g key="angle-indicators">
        {angles.map(angleData => (
          <g key={`angle-${angleData.index}`}>
            {/* Angle arc */}
            <circle
              cx={angleData.point.x}
              cy={angleData.point.y}
              r={15}
              fill="none"
              stroke={theme.palette.warning.main}
              strokeWidth={1}
              strokeDasharray="3,3"
              opacity={0.7}
            />

            {/* Angle text */}
            <text
              x={angleData.point.x + 20}
              y={angleData.point.y - 20}
              fill={theme.palette.warning.main}
              fontSize="9"
              fontWeight="600"
            >
              {angleData.angle}°
            </text>
          </g>
        ))}
      </g>
    );
  }, [showAngles, workingPoints, theme]);

  // Render construction grid helpers
  const renderGridHelpers = useMemo(() => {
    if (workingPoints.length === 0) return null;

    // Find bounding box
    let minX = Infinity,
      maxX = -Infinity;
    let minY = Infinity,
      maxY = -Infinity;

    workingPoints.forEach(point => {
      minX = Math.min(minX, point.x);
      maxX = Math.max(maxX, point.x);
      minY = Math.min(minY, point.y);
      maxY = Math.max(maxY, point.y);
    });

    const width = maxX - minX;
    const height = maxY - minY;

    return (
      <g key="grid-helpers" opacity={0.3}>
        {/* Bounding box */}
        <rect
          x={minX}
          y={minY}
          width={width}
          height={height}
          fill="none"
          stroke={theme.palette.info.main}
          strokeWidth={1}
          strokeDasharray="5,5"
        />

        {/* Dimension lines */}
        <g>
          {/* Width dimension */}
          <line
            x1={minX}
            y1={maxY + 30}
            x2={maxX}
            y2={maxY + 30}
            stroke={theme.palette.info.main}
            strokeWidth={1}
          />
          <text
            x={(minX + maxX) / 2}
            y={maxY + 45}
            textAnchor="middle"
            fill={theme.palette.info.main}
            fontSize="10"
            fontFamily="monospace"
          >
            {calculateRealWorldDistance(width, userScale).toFixed(2)}'
          </text>

          {/* Height dimension */}
          <line
            x1={minX - 30}
            y1={minY}
            x2={minX - 30}
            y2={maxY}
            stroke={theme.palette.info.main}
            strokeWidth={1}
          />
          <text
            x={minX - 45}
            y={(minY + maxY) / 2}
            textAnchor="middle"
            fill={theme.palette.info.main}
            fontSize="10"
            fontFamily="monospace"
            transform={`rotate(-90, ${minX - 45}, ${(minY + maxY) / 2})`}
          >
            {calculateRealWorldDistance(height, userScale).toFixed(2)}'
          </text>
        </g>
      </g>
    );
  }, [workingPoints, userScale, theme]);

  if (!shouldRender) return null;

  return (
    <g key="area-drawing-enhancements">
      {renderSegmentLabels}
      {renderAngleIndicators}
      {renderGridHelpers}
    </g>
  );
};
